export function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

function unwrapPayload(data, preferredKey) {
  if (!data || typeof data !== "object") return {};
  return data[preferredKey] || data.result?.[preferredKey] || data.result || data.data || data;
}

function joinValues(value) {
  return toArray(value)
    .map((item) => String(item))
    .filter(Boolean)
    .join(", ");
}

function valueOrDash(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function statusFromPassed(passed) {
  if (passed === true) return "PASS";
  if (passed === false) return "FAIL";
  return "UNKNOWN";
}

function tokenRelation(tokenId, queryTokenIds, relatedTokenIds, fallback) {
  if (fallback) return String(fallback);
  const id = String(tokenId ?? "");
  if (id && queryTokenIds.map(String).includes(id)) return "query_token";
  if (id && relatedTokenIds.map(String).includes(id)) return "related_token";
  return "";
}

function normalizeLifecycleItem(item, queryTokenIds, relatedTokenIds) {
  const tokenId = item?.tokenId ?? item?.nftTokenId ?? item?.id ?? "";
  return {
    tokenId: valueOrDash(tokenId),
    relationToQuery: tokenRelation(tokenId, queryTokenIds, relatedTokenIds, item?.relationToQuery),
    retired: Boolean(item?.retired),
    retiredBy: valueOrDash(item?.retiredBy),
    currentOwner: valueOrDash(item?.currentOwner),
    mintEventCount: Number(item?.mintEventCount || 0),
    transferEventCount: Number(item?.transferEventCount || 0),
    marketplaceEventCount: Number(item?.marketplaceEventCount || 0),
    retirementEventCount: Number(item?.retirementEventCount || 0),
  };
}

function normalizeDevice(item) {
  return {
    deviceId: valueOrDash(item?.deviceId ?? item?.device ?? item?.address ?? item?.hepekID),
    lineageStatus: valueOrDash(item?.lineageStatus),
    mintedAmount: valueOrDash(item?.mintedAmount),
    mintedEventCount: Number(item?.mintedEventCount || 0),
    bridgeConsumedAmount: valueOrDash(item?.bridgeConsumedAmount),
    bridgeConsumedEventCount: Number(item?.bridgeConsumedEventCount || 0),
    firstMintBlockNumber: valueOrDash(item?.firstMintBlockNumber),
    lastMintBlockNumber: valueOrDash(item?.lastMintBlockNumber),
  };
}

function normalizeBridgedLot(item) {
  return {
    lotId: valueOrDash(item?.lotId ?? item?.messageId ?? item?.id),
    lineageStatus: valueOrDash(item?.lineageStatus),
    sourceMintedAmount: valueOrDash(item?.sourceMintedAmount),
    sourceConsumedAmount: valueOrDash(item?.sourceConsumedAmount),
    mintEventCount: Number(item?.mintEventCount || 0),
    consumptionEventCount: Number(item?.consumptionEventCount || 0),
    sourceDevices: joinValues(item?.sourceDevices),
  };
}

function normalizeCheck(item) {
  const name = item?.name || item?.label || item?.key || "";
  if (!name) return null;
  const status = statusFromPassed(item?.passed);
  return {
    name,
    passed: item?.passed,
    status,
    details: item?.details || "",
  };
}

export function buildHepekProvenanceViewModel({ discover, audit, provenance, txHash = "", requestErrors = [] } = {}) {
  const d = unwrapPayload(discover, "discover");
  const a = unwrapPayload(audit, "audit");
  const p = unwrapPayload(provenance, "provenance");

  const queryTokenIds = toArray(p?.queryTokenIds);
  const relatedTokenIds = toArray(p?.relatedTokenIds);
  const warnings = toArray(p?.warnings).filter(Boolean);
  const limitations = toArray(a?.limitations).filter(Boolean);
  const checks = toArray(a?.checks).map(normalizeCheck).filter(Boolean);
  const lotsMissingMintLineage = toArray(p?.lotsMissingMintLineage);
  const warningCards = warnings.map((warning) => ({
    message: String(warning),
    lotsMissingMintLineage: /missing mint lineage/i.test(String(warning)) ? lotsMissingMintLineage : [],
  }));
  const valid = a?.valid;

  return {
    ok: Boolean(discover || audit || provenance),
    auditVersion: "v2",
    source: "hepek-provenance",
    txHash,
    verdict: valid === true ? "PASS" : valid === false ? "FAIL" : "UNKNOWN",
    summary: a?.summary || null,
    metadata: {
      network: joinValues(d?.networks),
      chain: joinValues(d?.chainIds),
      type: joinValues(d?.transactionTypes),
      eventKinds: joinValues(d?.eventKinds),
    },
    queryTokenIds,
    relatedTokenIds,
    checks,
    warnings,
    warningCount: warnings.length,
    warningCards,
    clean: warnings.length === 0,
    limitations,
    nftLifecycle: toArray(p?.nftLifecycle).map((item) => normalizeLifecycleItem(item, queryTokenIds, relatedTokenIds)),
    contributingDevices: toArray(p?.contributingDevices).map(normalizeDevice),
    bridgedLots: toArray(p?.bridgedLots).map(normalizeBridgedLot),
    lotsMissingMintLineage,
    canonical: p?.canonicalRecords || p?.canonical || p?.digest || a?.canonical || null,
    requestErrors: toArray(requestErrors),
    raw: { discover: d, audit: a, provenance: p },
  };
}
